
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CheckoutHookOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  returnUrl?: string;
}

interface CheckoutError extends Error {
  type?: 'authentication' | 'network' | 'stripe' | 'unknown';
}

function useCheckout(options: CheckoutHookOptions = {}) {
  const handleError = (error: CheckoutError) => {
    let message = 'Failed to start checkout process';
    
    switch (error.type) {
      case 'authentication':
        message = 'Please sign in to continue';
        break;
      case 'network':
        message = 'Network error. Please check your connection';
        break;
      case 'stripe':
        message = 'Payment service error. Please try again';
        break;
    }

    toast.error(message);
    options.onError?.(error);
  };

  const startSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          returnUrl: options.returnUrl || '/dashboard',
        }
      });
      
      if (error) {
        throw Object.assign(new Error(error.message), { 
          type: error.message.includes('authenticated') ? 'authentication' : 'unknown' 
        });
      }
      
      if (data?.url) {
        window.location.href = data.url;
        options.onSuccess?.();
      } else {
        throw Object.assign(new Error('No checkout URL received'), { 
          type: 'stripe' 
        });
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      handleError(error as CheckoutError);
    }
  };

  return {
    startSubscription
  };
}

export default useCheckout;
